import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    HOVER_INTENT_HIDE_MS,
    HOVER_INTENT_SHOW_MS,
    createHoverIntentController,
    type HoverIntentController,
} from './hover-intent-core';

export {
    HOVER_INTENT_HIDE_MS,
    HOVER_INTENT_SHOW_MS,
    HOVER_INTENT_WARM_MS,
} from './hover-intent-core';

export type UseHoverIntentOptions = {
    /** Cold reveal delay. Defaults to `HOVER_INTENT_SHOW_MS`. */
    showDelay?: number;
    /** Grace period before hiding. Defaults to `HOVER_INTENT_HIDE_MS`. */
    hideDelay?: number;
    /** Never reveal (read-only previews, the hidden measurement pass). */
    disabled?: boolean;
    /** Hold the chrome open regardless of the pointer — drags, open menus. */
    keepOpen?: boolean;
};

export type UseHoverIntentResult = {
    isActive: boolean;
    /** Spread on the group root that owns the chrome. */
    hoverProps: {
        onMouseEnter: () => void;
        onMouseLeave: () => void;
    };
    /**
     * Spread on the same root. Children gate off it with
     * `group-data-[hover-intent=on]/<name>:opacity-100`.
     */
    dataProps: {
        'data-hover-intent': 'on' | undefined;
    };
    /** Reveal without waiting — keyboard focus, imperative reveals. */
    showNow: () => void;
    /** Hide after the usual grace period. */
    hide: () => void;
};

/**
 * Hover chrome that waits for intent before appearing.
 *
 * Put it on the *group root* rather than on each control, so moving between
 * the controls of one block never re-arms the delay. See `hover-intent-core`
 * for the shared-warmth rules.
 */
export function useHoverIntent(options: UseHoverIntentOptions = {}): UseHoverIntentResult {
    const {
        showDelay = HOVER_INTENT_SHOW_MS,
        hideDelay = HOVER_INTENT_HIDE_MS,
        disabled = false,
        keepOpen = false,
    } = options;

    const [isActive, setIsActive] = useState(false);
    const controllerRef = useRef<HoverIntentController | null>(null);
    const optionsRef = useRef({ showDelay, hideDelay, disabled });
    optionsRef.current = { showDelay, hideDelay, disabled };

    // Resolved lazily rather than held in a const: StrictMode's
    // mount → cleanup → mount runs the unmount cleanup on a component that is
    // about to live on, so a controller disposed there has to be replaced
    // instead of leaving every handler wired to a dead one.
    const getController = useCallback(() => {
        const existing = controllerRef.current;
        if (existing && !existing.isDisposed()) return existing;
        const created = createHoverIntentController(setIsActive, optionsRef.current);
        controllerRef.current = created;
        return created;
    }, []);

    useEffect(() => {
        getController().configure({ showDelay, hideDelay, disabled });
    }, [getController, showDelay, hideDelay, disabled]);

    useEffect(() => {
        getController().setKeepOpen(keepOpen);
    }, [getController, keepOpen]);

    useEffect(
        () => () => {
            controllerRef.current?.dispose();
        },
        [],
    );

    const onMouseEnter = useCallback(() => getController().enter(), [getController]);
    const onMouseLeave = useCallback(() => getController().leave(), [getController]);
    const showNow = useCallback(() => getController().showNow(), [getController]);
    const hide = useCallback(() => getController().leave(), [getController]);

    const hoverProps = useMemo(
        () => ({ onMouseEnter, onMouseLeave }),
        [onMouseEnter, onMouseLeave],
    );
    const dataProps = useMemo(
        () => ({ 'data-hover-intent': isActive ? ('on' as const) : undefined }),
        [isActive],
    );

    return { isActive, hoverProps, dataProps, showNow, hide };
}
